import React, { useState, useEffect } from 'react';
import { StatusPieChart, ExpiryTimelineChart, CertificateTrendChart } from '../components/ChartComponents';
import { ExpiryCalendar } from '../components/ExpiryCalendar';
import { SkeletonCard } from '../components/ui/skeleton';
import { certificatesAPI } from '../lib/api';
import { calculateDaysRemaining, getCertificateStatus } from '../lib/utils';
import { STATUS } from '../lib/theme';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';

// Proyeksi kedaluwarsa N bulan ke depan berdasarkan data sertifikat yang ada
// (bukan histori asli - backend tidak menyimpan snapshot historis - melainkan
// proyeksi maju dari expiration_date tiap sertifikat yang masih aktif hari ini).
function generateExpiryProjection(certificates, monthsAhead = 6) {
  const now = new Date();
  const active = certificates.filter(cert => calculateDaysRemaining(cert.expiration_date) >= 0);
  let remaining = active.length;

  return Array.from({ length: monthsAhead }, (_, i) => {
    const bucketStart = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const bucketEnd = new Date(now.getFullYear(), now.getMonth() + i + 1, 1);
    const rangeStart = i === 0 ? now : bucketStart;

    const expiringCount = active.filter(cert => {
      const expiry = new Date(cert.expiration_date);
      return expiry >= rangeStart && expiry < bucketEnd;
    }).length;

    remaining -= expiringCount;

    return {
      label: format(bucketStart, 'MMM yyyy', { locale: id }),
      expiring: expiringCount,
      total: Math.max(0, remaining),
    };
  });
}

export function StatisticsPage() {
  const [loading, setLoading] = useState(true);
  const [certificates, setCertificates] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await certificatesAPI.getAll();
      setCertificates(response.data);
    } catch (error) {
      toast.error('Gagal memuat data statistik');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div>
          <div className="h-5 w-48 bg-muted animate-pulse rounded-sm mb-2" />
          <div className="h-3 w-72 bg-muted animate-pulse rounded-sm" />
        </div>
        <div className="grid gap-3 grid-cols-1 lg:grid-cols-3">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
        <div className="h-96 w-full bg-muted animate-pulse rounded-sm" />
      </div>
    );
  }

  const validCerts = certificates.filter(cert => getCertificateStatus(calculateDaysRemaining(cert.expiration_date)) === 'valid').length;
  const warningCerts = certificates.filter(cert => getCertificateStatus(calculateDaysRemaining(cert.expiration_date)) === 'warning').length;
  const criticalCerts = certificates.filter(cert => getCertificateStatus(calculateDaysRemaining(cert.expiration_date)) === 'critical').length;
  const expiredCerts = certificates.filter(cert => getCertificateStatus(calculateDaysRemaining(cert.expiration_date)) === 'expired').length;

  const statusDistribution = {
    valid: validCerts,
    warning: warningCerts,
    critical: criticalCerts,
    expired: expiredCerts,
  };

  const projectionData = generateExpiryProjection(certificates);

  return (
    <div className="space-y-4">
      {/* Breadcrumb + Page Header */}
      <div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
          <span>Manajemen</span>
          <span>›</span>
          <span style={{ color: STATUS.primary.text }} className="font-medium">Statistik & Kalender</span>
        </div>
        <h2 className="text-base sm:text-lg font-semibold tracking-tight">Statistik & Kalender</h2>
        <p className="text-muted-foreground mt-0.5 text-xs">
          Analitik distribusi status, proyeksi kedaluwarsa, dan kalender jatuh tempo sertifikat SSL
        </p>
      </div>

      {/* Analytics Row */}
      <div className="grid gap-3 grid-cols-1 lg:grid-cols-3 lg:items-stretch">
        <StatusPieChart data={statusDistribution} />
        <ExpiryTimelineChart certificates={certificates} />
        <CertificateTrendChart
          data={projectionData}
          title="Proyeksi Kedaluwarsa (6 Bulan)"
          totalLabel="Sertifikat Aktif"
          expiringLabel="Expire Bulan Ini"
        />
      </div>

      {/* Kalender Kedaluwarsa - full width */}
      <ExpiryCalendar certificates={certificates} />
    </div>
  );
}
